'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/probability'

const SurpriseCurve = dynamic(() => import('../_components/widgets/ProbWidgets').then(m => m.SurpriseCurve), { ssr: false })
const CrossEntropyViz = dynamic(() => import('../_components/widgets/ProbWidgets').then(m => m.CrossEntropyViz), { ssr: false })

export default function CrossEntropy() {
  return (
    <Lesson>
      <Step gate="q-surprise">
        <H>Measuring surprise</H>
        <P>
          If something you thought was 99% likely happens, you’re not surprised. If something you thought was 1% likely happens,
          you’re very surprised. Information theory turns this into a number:
        </P>
        <MB>{'\\pink{\\text{surprise}} = -\\ln(\\violet{p})'}</MB>
        <P>
          The logarithm of a probability is negative (probabilities are below 1), so the minus sign makes surprise positive. And
          it has exactly the right shape: certain events (p = 1) have zero surprise; impossible-seeming ones explode.
        </P>
        <Figure n="5.10" caption="Drag the probability the model assigned to what actually happened. Above about 0.5 the surprise is gentle; near zero it shoots up.">
          <SurpriseCurve />
        </Figure>
        <Quiz
          id="q-surprise"
          question="A weather model said rain was 10% likely, and then it rained. Its surprise is −ln(0.1). Roughly how big is that?"
          options={[{ label: 'About 2.3', correct: true }, { label: 'About 0.1', why: 'That’s the probability itself. Surprise is −ln of it.' }, { label: 'About 10', why: 'ln(0.1) ≈ −2.3, so the surprise is about 2.3.' }]}
          explain="−ln(0.1) ≈ 2.303. Keep that number in mind…"
        />
      </Step>

      <Step>
        <H>Cross-entropy loss</H>
        <P>
          Here is how we score a model that outputs probabilities: look at the probability it gave to the{' '}
          <strong>correct</strong> answer, and measure the surprise.
        </P>
        <MB>{'\\pink{L} = -\\ln\\big(\\violet{p_{\\text{correct}}}\\big)'}</MB>
        <P>
          That’s <strong>cross-entropy loss</strong>. Averaged over a whole dataset, it’s the loss used to train practically
          every classifier and every language model in existence. Play with it:
        </P>
        <Figure n="5.11" caption="A model’s predicted probabilities, and the true answer (amber). The loss only cares about the probability on the true answer — but softmax means raising it lowers the others.">
          <CrossEntropyViz />
        </Figure>
        <Callout kind="plain">
          Being <em>confidently wrong</em> is punished brutally: if the model put 0.1% on the right answer, the loss is about 6.9.
          Being honestly unsure (25% on four options) costs about 1.4. Cross-entropy rewards calibrated, honest probabilities.
        </Callout>
      </Step>

      <Step gate="cross-entropy">
        <H>Write it</H>
        <Exercise
          id="cross-entropy"
          title="crossEntropy"
          {...EX.crossEntropy}
          hints={[
            <>
              <C>return -Math.log(probs[target])</C>
            </>,
            <>
              From logits: <C>return crossEntropy(vec.softmax(scores), target)</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-meter">
        <H>About that loss meter…</H>
        <P>Look at the top-right corner of this page. Your “loss” has been sitting at 2.303 since you started (or a bit lower, by now). Here’s why:</P>
        <Exercise id="ten-classes" title="The loss of knowing nothing" {...EX.tenClasses} />
        <P>
          A model that knows nothing about a 10-way question spreads its bets evenly: 1/10 each. Its cross-entropy is{' '}
          <M>{'-\\ln(1/10) = \\ln 10 \\approx 2.303'}</M>. That’s where every untrained model starts — and where you started.
          Every lesson you finish pushes your loss toward 0. You are, quite literally, being trained.
        </P>
        <Quiz
          id="q-meter"
          question="A freshly initialised model must pick one of 50,000 words. Roughly what is its starting cross-entropy loss?"
          options={[{ label: 'ln(50000) ≈ 10.8', correct: true }, { label: '2.303', why: 'That’s for 10 equally likely options. With 50,000, guessing is much worse.' }, { label: '0', why: 'Zero loss means perfect predictions — not where an untrained model starts.' }]}
          explain="Uniform over N options gives loss ln N. Watching a language model’s loss fall from ~10.8 toward ~2 is watching it learn language."
        />
        <Callout kind="why">
          Why this particular loss, and not something else? In the next lesson you’ll see that minimising cross-entropy is the
          same as making the training data as <em>likely</em> as possible under the model — the single idea behind nearly every
          loss in machine learning.
        </Callout>
      </Step>
    </Lesson>
  )
}
